import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Loading from '../components/Loading';
import StudentEditDialog from '../components/Students/StudentEditDialog';
import StudentList from '../components/Students/StudentList';
import { toStudentDetail } from '../routes/paths';
import studentsService from '../services/students-service';
import { Student } from '../types/student';

const Students: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentToEdit, setStudentToEdit] = useState<Student | null | 'create'>(
    null
  );
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const navigate = useNavigate();

  const fetchStudents = () => {
    studentsService.getAll().then((students) => {
      setStudents(students);
      setLoading(false);
    });
  };

  useEffect(fetchStudents, []);

  const handleDelete = () => {
    if (!studentToDelete?.id) {
      throw new Error('Cannot delete a student without an ID');
    }

    setLoading(true);
    studentsService.remove(studentToDelete.id).then(() => {
      fetchStudents();
      setStudentToDelete(null);
    });
  };

  const handleCreate = (student: Student) => {
    setLoading(true);
    studentsService.add(student).then(() => {
      fetchStudents();
      setStudentToEdit(null);
    });
  };

  const handleEdit = (student: Student) => {
    setLoading(true);
    studentsService.update(student).then(() => {
      fetchStudents();
      setStudentToEdit(null);
    });
  };

  const renderDialog = () =>
    studentToDelete ? (
      <ConfirmationDialog
        onClose={() => setStudentToDelete(null)}
        onConfirm={handleDelete}
        title={'Smazat žáka?'}
        description={studentToDelete.name}
        confirmText="Smazat"
      />
    ) : studentToEdit ? (
      <StudentEditDialog
        onConfirm={studentToEdit === 'create' ? handleCreate : handleEdit}
        onClose={() => setStudentToEdit(null)}
        student={studentToEdit === 'create' ? null : studentToEdit}
      />
    ) : null;

  return (
    <>
      {renderDialog()}
      <Typography variant="h4" mb={2}>
        Přehled žáků
      </Typography>
      <Box display="flex" mb={2}>
        <Button
          variant="contained"
          disabled={loading}
          onClick={() => setStudentToEdit('create')}
        >
          <AddIcon />
        </Button>
      </Box>
      {loading && students.length === 0 ? (
        <Loading />
      ) : (
        <>
          {students.length === 0 ? (
            <Typography variant="h5">Žádní žáci</Typography>
          ) : (
            <StudentList
              students={students}
              onRating={(student) =>
                navigate(toStudentDetail(student.id!), { state: student })
              }
              onEdit={(student) => setStudentToEdit(student)}
              onDelete={(student) => setStudentToDelete(student)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Students;
